import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table as MuiTable,
  Box,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import sortBy from "lodash-es/sortBy";
import { ReactElement, useMemo, useState } from "react";

import { palette } from "~frontend/theme";

import errorCodeDescriptions from "./errorCodeDescriptions";
import { StatusTableHeaderCell } from "./styles";
import { MessageStatusBreakdown } from "./types";

export default function ErrorCodeTable({
  messageStatusBreakdown,
}: {
  messageStatusBreakdown: MessageStatusBreakdown;
}): ReactElement {
  const [byCarrier, setByCarrier] = useState(false);

  const data: {
    count: number;
    errorCode: string;
    carrier?: string;
  }[] = useMemo(() => {
    if (byCarrier) {
      return messageStatusBreakdown.sentFailed;
    }

    const errorsByCode: { [errorCode: string]: number } = {};
    messageStatusBreakdown.sentFailed.forEach(entry => {
      if (!errorsByCode[entry.errorCode]) {
        errorsByCode[entry.errorCode] = 0;
      }
      errorsByCode[entry.errorCode] += entry.count;
    });

    return Object.keys(errorsByCode).map(errorCode => ({
      count: errorsByCode[errorCode],
      errorCode,
    }));
  }, [byCarrier, messageStatusBreakdown]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: palette.midGray,
      }}
    >
      <Box
        sx={{
          paddingLeft: "14px",
          paddingTop: "8px",
          paddingBottom: "0px",
          backgroundColor: palette.midGray,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ fontWeight: "bold" }}>Error Codes</Box>
        <Box sx={{ marginTop: "-8px" }}>
          <FormControlLabel
            control={
              <Switch
                value={byCarrier}
                onChange={(_evt, checked) => setByCarrier(checked)}
                sx={{
                  "& .MuiSwitch-track": {
                    backgroundColor: `${palette.lightestGray} !important`,
                  },
                }}
              />
            }
            label="Break down by carrier"
          />
        </Box>
      </Box>
      <Box sx={{ padding: "20px", backgroundColor: palette.white }}>
        <MuiTable>
          <TableHead>
            <TableRow>
              {byCarrier && (
                <StatusTableHeaderCell>Carrier</StatusTableHeaderCell>
              )}
              <StatusTableHeaderCell>Error Description</StatusTableHeaderCell>
              <StatusTableHeaderCell sx={{ width: "100px" }}>
                Error Count
              </StatusTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortBy(data, d => -d.count).map(
              ({ count, errorCode, carrier }) => {
                const errorDetails = errorCodeDescriptions[errorCode] || {
                  code: errorCode,
                  name: "Unknown Error",
                  description: "",
                };

                return (
                  <TableRow key={errorCode}>
                    {byCarrier && <TableCell>{carrier}</TableCell>}
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        {errorDetails.name} ({errorDetails.code})
                      </Typography>
                      {errorDetails.description}
                    </TableCell>
                    <TableCell>{count}</TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </MuiTable>
      </Box>
    </Box>
  );
}
